import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { policies, idCards, cardLabel, fmtMD, fmtMDY, telHref, extraDocLabel } from './policyCalc';
import { loadPolicyRows, cachedPolicyRows, cachedImage, fetchImage, vaultWebUrl } from './insuranceApi';
import './Insurance.css';

// The household insurance dash. Top: the auto ID card, no tap needed — it
// draws from the device cache first, so it is on screen before the network
// answers (or when it never does). Below: one card per policy. Read-only;
// every fact comes from Household_Policies and the Family Vault.

// ── ID cards ──────────────────────────────────────────────────────────────────
function IdCards({ cards, images, offline }) {
  const [index, setIndex] = useState(0);
  const [full, setFull] = useState(null);   // path shown full screen
  const track = useRef(null);

  const onScroll = () => {
    const el = track.current;
    if (el && el.clientWidth) setIndex(Math.round(el.scrollLeft / el.clientWidth));
  };
  const go = i => {
    const el = track.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    setIndex(i);
  };

  useEffect(() => {
    if (!full) return undefined;
    const onKey = e => { if (e.key === 'Escape') setFull(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  if (!cards.length) return null;
  const current = cards[Math.min(index, cards.length - 1)];
  const p = current.policy;

  return (
    <section className="ins-id" aria-label="Auto ID cards">
      <div className="ins-id-track" ref={track} onScroll={onScroll}>
        {cards.map(c => (
          <button key={c.path} className="ins-id-slide" onClick={() => images[c.path] && setFull(c.path)}
            aria-label={`${cardLabel(c.path)} ID card, tap for full screen`}>
            {images[c.path]
              ? <img src={images[c.path]} alt={`${cardLabel(c.path)} insurance ID card`} />
              : <span className="ins-id-wait" />}
          </button>
        ))}
      </div>

      <div className="ins-id-meta">
        {cards.length > 1 && (
          <div className="ins-id-tabs" role="tablist">
            {cards.map((c, i) => (
              <button key={c.path} role="tab" aria-selected={i === index}
                className={`ins-id-tab${i === index ? ' is-on' : ''}`} onClick={() => go(i)}>
                {cardLabel(c.path)}
              </button>
            ))}
          </div>
        )}
        <div className="ins-id-line">
          <span>#{p.number}</span>
          <span>{fmtMDY(p.eff)}–{fmtMDY(p.exp)}</span>
          {p.notStarted && <span className="ins-chip ins-chip--amber">starts {fmtMD(p.eff)}</span>}
          {offline && <span className="ins-chip ins-chip--muted">saved copy</span>}
        </div>
      </div>

      {full && (
        <div className="ins-full" role="dialog" aria-label="ID card, full screen" onClick={() => setFull(null)}>
          <img src={images[full]} alt={`${cardLabel(full)} insurance ID card`} />
        </div>
      )}
    </section>
  );
}

// ── Policy card ───────────────────────────────────────────────────────────────
const RING = 2 * Math.PI * 20;

function Ring({ p }) {
  const left = p.termLeft ?? 0;
  return (
    <svg className={`ins-ring ins-ring--${p.tone}`} viewBox="0 0 48 48" role="img"
      aria-label={p.toRenew === null ? 'No renewal date' : `${p.toRenew} days to renewal`}>
      <circle cx="24" cy="24" r="20" className="ins-ring-track" />
      <circle cx="24" cy="24" r="20" className="ins-ring-fill"
        strokeDasharray={RING} strokeDashoffset={RING * (1 - left)} transform="rotate(-90 24 24)" />
      <text x="24" y="24" textAnchor="middle" dominantBaseline="central">{p.toRenew ?? '—'}</text>
    </svg>
  );
}

function PolicyCard({ p, onOpenDoc }) {
  const [showOpen, setShowOpen] = useState(false);
  return (
    <article className={`ins-policy ins-lob--${p.lob.toLowerCase()}`}>
      <header className="ins-policy-head">
        <div>
          <h3 className="ins-policy-title">
            {p.title}
            {p.openItems.length > 0 && (
              <button className="ins-open-dot" aria-expanded={showOpen}
                aria-label={`${p.openItems.length} open item${p.openItems.length === 1 ? '' : 's'}`}
                onClick={() => setShowOpen(v => !v)} />
            )}
          </h3>
          <div className="ins-policy-num">#{p.number}</div>
          <div className="ins-policy-sub">{p.carrier}</div>
          <div className="ins-policy-dates">
            {fmtMDY(p.eff)}–{fmtMDY(p.exp)}
            {p.notStarted && <span className="ins-chip ins-chip--amber">starts {fmtMD(p.eff)}</span>}
          </div>
        </div>
        <Ring p={p} />
      </header>

      {showOpen && (
        <ul className="ins-open-list">
          {p.openItems.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}

      {p.limits.length > 0 && (
        <div className="ins-limits">
          {p.limits.map((l, i) => <span key={i} className="ins-limit">{l}</span>)}
        </div>
      )}
      {p.premium && <div className="ins-premium">{p.premium}</div>}

      <div className="ins-actions">
        {p.decPath && <button className="ins-btn" onClick={() => onOpenDoc(p.decPath)}>Dec page</button>}
        {p.fullPaths.length > 0 && <button className="ins-btn" onClick={() => onOpenDoc(p.fullPaths[0])}>Full policy</button>}
        {p.agentPhone && <a className="ins-btn" href={telHref(p.agentPhone)}>Call agent</a>}
        {p.claimsPhone && <a className="ins-btn ins-btn--quiet" href={telHref(p.claimsPhone)}>Claims</a>}
      </div>
      {p.fullPaths.length > 1 && (
        <button className="ins-doc-chip" onClick={() => onOpenDoc(p.fullPaths[1])}>{extraDocLabel(p.fullPaths[1])}</button>
      )}
    </article>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Insurance() {
  const { instance, accounts } = useMsal();
  const [rows, setRows] = useState(() => cachedPolicyRows());
  const [images, setImages] = useState({});        // path → object URL
  const [offline, setOffline] = useState(false);   // showing a saved copy
  const [error, setError] = useState(null);
  const urls = useRef({});

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  const show = useCallback((path, blob) => {
    const url = URL.createObjectURL(blob);
    if (urls.current[path]) URL.revokeObjectURL(urls.current[path]);
    urls.current[path] = url;
    setImages(prev => ({ ...prev, [path]: url }));
  }, []);

  useEffect(() => () => { Object.values(urls.current).forEach(u => URL.revokeObjectURL(u)); }, []);

  const list = policies(rows || []);
  const cards = idCards(list);
  const cardKey = cards.map(c => c.path).join('|');

  // 1. Saved copies first, so the card is up before the network answers.
  useEffect(() => {
    let live = true;
    (cardKey ? cardKey.split('|') : []).forEach(c => {
      cachedImage(c).then(b => { if (live && b && !urls.current[c]) show(c, b); });
    });
    return () => { live = false; };
  }, [cardKey, show]);

  // 2. Then the live rows.
  useEffect(() => {
    let live = true;
    getToken()
      .then(loadPolicyRows)
      .then(r => { if (live) { setRows(r); setError(null); } })
      .catch(e => { if (live) setError(e.message || String(e)); });
    return () => { live = false; };
  }, [getToken]);

  // 3. And fresh images for whatever the rows name.
  useEffect(() => {
    if (!cardKey) return undefined;
    let live = true;
    getToken()
      .then(token => Promise.all(cardKey.split('|').map(c => fetchImage(token, c).then(b => { if (live) show(c, b); }))))
      .then(() => { if (live) setOffline(false); })
      .catch(() => { if (live) setOffline(true); });
    return () => { live = false; };
  }, [cardKey, getToken, show]);

  // Open the window inside the tap (iOS blocks one opened after an await),
  // then point it at the vault file.
  const openDoc = async (path) => {
    const win = window.open('', '_blank');
    try {
      const url = await vaultWebUrl(await getToken(), path);
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) {
      if (win) win.close();
      setError(e.message || String(e));
    }
  };

  return (
    <div className="ins">
      <IdCards cards={cards} images={images} offline={offline} />

      {error && !rows && (
        <p className="ins-note">
          {/No FIG list definition|not found/i.test(error) ? 'The Household_Policies list is not set up yet.' : `Couldn't load policies: ${error}`}
        </p>
      )}
      {error && rows && <p className="ins-note ins-note--quiet">Offline: showing the last saved copy.</p>}
      {!rows && !error && <p className="ins-note ins-note--quiet">Loading…</p>}

      <div className="ins-policies">
        {list.map(p => <PolicyCard key={p.id || p.number} p={p} onOpenDoc={openDoc} />)}
      </div>
    </div>
  );
}
